import * as React from "react";
import { StyleSheet } from "react-native";
import Colors from "../../constants/Colors";
import { Text, View } from "../Themed";
import { Feather } from "@expo/vector-icons";
import useThemeColor from "../../hooks/useThemeColor";
import Layout from "../../constants/Layout";
import moment, { Moment } from "moment";
import GlobalContext from "../../context/Global";
import Pressable from "../Pressable";

type Props = {
  address: any;
  startTime: string;
  endTime: string;
  date: string;
  attendees?: number;
  hours: number;
  completed?: boolean;
  onPress: () => void;
  syncRequired?: boolean;
  sync?: () => void;
  syncing?: boolean;
  item?: any;
};

export default function OpenHouseListItem(props: Props) {
  const {
    address,
    date,
    attendees = 0,
    hours = 0,
    completed = false,
    startTime = "00:00",
    endTime = "00:00",
    onPress,
    syncRequired = false,
    sync,
    syncing = false,
    item,
  } = props;

  const { isTablet } = React.useContext(GlobalContext);

  const formatTime = (time: Moment) => {
    return moment(time, "HH:mm:ss").format("h:mm A");
  };

  const ArrowIcon = () => (
    <Feather
      name="chevron-right"
      size={24}
      color={useThemeColor(
        { light: Colors.nuetral_grey_700, dark: Colors.base_colors_white },
        "background"
      )}
    />
  );

  const Date = () => {
    const parsedDate = moment(date, [
      "YYYY/MM/DD",
      "YYYY-MM-DDTHH:mm:ss.SSSZ",
    ]).startOf("day");
    const currentDate = moment().startOf("day");

    const parseTime = (time: string) => {
      if (moment(time, "HH:mm:ss", true).isValid()) {
        return moment(time, "HH:mm:ss");
      }
      return moment(time);
    };

    const formatStart = parseTime(startTime);
    const formatEnd = parseTime(endTime);

    return (
      <Text lightColor={Colors.nuetral_grey_700}>
        {parsedDate.diff(currentDate, "days") === 0
          ? `Today, ${formatTime(formatStart)} - ${formatTime(formatEnd)}`
          : `${parsedDate.format("ddd, MMMM DD, YYYY")} ${formatTime(
              formatStart
            )} - ${formatTime(formatEnd)}`}
      </Text>
    );
  };

  const Address = () => (
    <Text size={16} weight={"Bold"} lightColor={Colors.nuetral_grey_700}>
      {address?.street_address} {address?.city} {address?.state},{" "}
      {address?.postal_code}
    </Text>
  );

  const Stat = ({ value, text }: { text: string; value: number }) => (
    <View style={styles.stats}>
      <Text size={16} weight={"Bold"} lightColor={Colors.nuetral_grey_700}>
        {value}
      </Text>
      <Text lightColor={Colors.nuetral_grey_700}>{text}</Text>
    </View>
  );

  return (
    <Pressable onPress={() => onPress()}>
      <View
        lightColor={Colors.base_colors_white}
        darkColor={Colors.nuetral_grey_700}
        style={[
          styles.container,
          {
            paddingHorizontal: isTablet ? 32 : 16,
          },
        ]}
      >
        <View style={styles.openHouseInfoContainer}>
          <View style={styles.addressDateContainer}>
            <Address />
            <Date />
            {syncRequired && (
              <Pressable onPress={sync} disabled={syncing} accessibilityState={{ disabled: syncing, busy: syncing }} style={styles.syncButton}>
                <Text
                  size={12}
                  lightColor={Colors.primary_500}
                  darkColor={Colors.primary_500}
                >
                  {syncing ? "Syncing…" : "Sync to Brivity CRM"}
                </Text>
              </Pressable>
            )}
          </View>

          {completed && (
            <View style={styles.attendeeHoursContainer}>
              <>
                <Stat text={"attendees"} value={attendees} />
                <Stat text={"hours"} value={hours} />
              </>
            </View>
          )}
        </View>
        <View style={styles.iconContainer}>
          <ArrowIcon />
        </View>
      </View>
    </Pressable>
  );
}

const styles = StyleSheet.create({
  container: {
    flexDirection: "row",
    gap: 24,
    paddingVertical: 16,
  },
  openHouseInfoContainer: {
    flex: 1,
    gap: 8,
    flexDirection: Layout.isLargeDevice ? "row" : "column",
    alignItems: Layout.isLargeDevice ? "center" : "flex-start",
    justifyContent: "space-between",
  },
  addressDateContainer: {
    gap: 8,
  },
  attendeeHoursContainer: {
    flexDirection: "row",
    gap: 24,
  },
  stats: {
    flexDirection: "row",
    gap: 4,
  },
  iconContainer: {
    alignItems: "center",
    justifyContent: "center",
  },
  syncButton: {
    backgroundColor: Colors.base_colors_white,
    borderWidth: 1,
    borderColor: Colors.primary_500,
    borderRadius: 22,
    padding: 4,
    width: 125, //I'm sure this will bite me in the butt
    justifyContent: "center",
    alignContent: "center",
    alignItems: "center",
  },
});
